/**
 * Concession disclaimer copy — LOC-1655 / LOC-1661 / LOC-1666 / LOC-1684
 *
 * DO NOT EDIT THE STRINGS BELOW WITHOUT LEGAL/COMPLIANCE SIGN-OFF.
 * Wording is locked per ticket AC. Any change requires a new sign-off
 * recorded against this file in source control.
 *
 * Structure:
 *   heading         — section title shown above the body
 *   body            — main descriptive paragraph (state-specific intro)
 *   eligibilityNote — line stating who qualifies / what they get
 *   eligibilityLink — LOC-1684 guide link appended below the last line of
 *                     the always-visible "Concession cards" alert
 *                     (SA/QLD/NSW/ACT only — not VIC).
 *   footerNote      — optional fine-print footnote (SA only)
 *   rebateInfo      — LOC-1666 "Concession and rebate information" alert
 *                     shown when "Yes" selected. Per-state copy describing
 *                     the rebates the customer auto-receives plus a link
 *                     to the eligibility guide. Optional bold phrases
 *                     wrap matching substrings of intro/secondaryIntro.
 */

import { State } from "../../Premises/types";

export interface RebateInfo {
  heading: string;
  intro: string;
  introBoldPhrases?: string[];
  primaryRebates?: string[];
  secondaryIntro?: string;
  secondaryIntroBoldPhrases?: string[];
  closingBeforeLink: string;
  linkLabel: string;
  linkUrl: string;
  closingAfterLink?: string;
  secondaryRebates?: string[];
}

export interface EligibilityLink {
  textBeforeLink: string;
  linkLabel: string;
  linkUrl: string;
  textAfterLink?: string;
}

export interface ConcessionDisclaimer {
  heading: string;
  body: string;
  eligibilityNote: string;
  eligibilityLink?: EligibilityLink;
  footerNote?: string;
  rebateInfo?: RebateInfo;
}

const eligibilityLink = (statePath: string): EligibilityLink => ({
  textBeforeLink:
    "For a guide on all programs available to you — including eligibility, applicable rates, and how to apply — visit ",
  linkLabel: "Concession and rebate eligibility",
  linkUrl: `https://www.originenergy.com.au/concessions-rebates/${statePath}`,
  textAfterLink: ".",
});

const VIC_REBATE_INFO: RebateInfo = {
  heading: "Concession and rebate information",
  intro:
    "As you hold an eligible concession card you'll automatically receive the Annual Electricity Concession and Winter Gas Concession, which will provide you with a discount on your energy usage.",
  introBoldPhrases: ["Annual Electricity Concession", "Winter Gas Concession"],
  closingBeforeLink:
    "For a guide on all programs available to you — including eligibility, applicable rates, and how to apply — visit ",
  linkLabel: "Concession and rebate eligibility",
  linkUrl: "https://www.originenergy.com.au/concessions-rebates/vic",
  closingAfterLink: ".",
};

const NSW_REBATE_INFO: RebateInfo = {
  heading: "Concession and rebate information",
  intro:
    "Thanks for providing your concession card details. If you have an electricity or gas account with us, we'll automatically apply the following:",
  primaryRebates: ["Low Income Household Rebate", "NSW Gas Rebate"],
  secondaryIntro:
    "There's a range of other NSW Government funded schemes available.",
  closingBeforeLink: "Visit ",
  linkLabel: "Concession and rebate eligibility",
  linkUrl: "https://www.originenergy.com.au/concessions-rebates/nsw",
  closingAfterLink: " for an up-to-date guide on the available programs.",
  secondaryRebates: [
    "Family Energy Rebate",
    "Seniors Energy Rebate",
    "Life Support Rebate",
    "Medical Energy Rebate",
    "Energy Accounts Payment Assistance (EAPA)",
  ],
};

const ACT_REBATE_INFO: RebateInfo = {
  heading: "Concession and rebate information",
  intro:
    "Thanks for providing your concession card details. We'll automatically apply the Electricity, Gas and Water Rebate to your account, which will help you reduce the cost of your energy bills.",
  introBoldPhrases: ["Electricity, Gas and Water Rebate"],
  secondaryIntro:
    "The ACT Government also funds the Life Support Rebate program.",
  closingBeforeLink: "Visit ",
  linkLabel: "Concession and rebate eligibility",
  linkUrl: "https://www.originenergy.com.au/concessions-rebates/act",
  closingAfterLink: " for more information.",
};

const QLD_REBATE_INFO: RebateInfo = {
  heading: "Concession and rebate information",
  intro:
    "Thanks for providing your concession card details. If you have an electricity or gas account with us, we'll automatically apply the Electricity and Gas Rebate to your account, which will help you reduce the cost of your energy bills.",
  introBoldPhrases: ["Electricity and Gas Rebate"],
  secondaryIntro:
    "There's a range of other Queensland Government funded schemes available.",
  closingBeforeLink: "Visit ",
  linkLabel: "Concession and rebate eligibility",
  linkUrl: "https://www.originenergy.com.au/concessions-rebates/qld",
  closingAfterLink: " for an up-to-date guide on the available programs.",
  secondaryRebates: [
    "Electricity Life Support Concession",
    "Medical Cooling and Heating Electricity Concession",
    "Home Energy Emergency Assistance Scheme",
  ],
};

const SA_REBATE_INFO: RebateInfo = {
  heading: "Concession and rebate information",
  intro:
    "Based on the details you've provided, you could be eligible for the Energy Bill Concession, Medical Heating and Cooling Concession or the Emergency Electricity Payment Scheme (EEPS) which will help you reduce the cost of your energy bills.",
  introBoldPhrases: [
    "Energy Bill Concession, Medical Heating and Cooling Concession",
    "Emergency Electricity Payment Scheme (EEPS)",
  ],
  closingBeforeLink:
    "Eligibility is assessed by ConcessionsSA. Once you receive your first bill, apply online at ",
  linkLabel: "Energy bill concessions - SA.GOV.AU",
  linkUrl:
    "https://www.sa.gov.au/topics/care-and-support/concessions/household-concessions/energy-bill-concessions",
};

export const CONCESSION_DISCLAIMERS: Record<State, ConcessionDisclaimer> = {
  [State.VIC]: {
    heading: "Concession cards",
    body: "The Vic Government provides a range of concessions, rebates, and relief schemes to help reduce energy costs. You may be eligible if you hold a card issued by Centrelink or the Department of Veterans' Affairs.",
    eligibilityNote:
      "Concession card holders in Victoria are eligible for Annual Electricity Concession and Winter Gas Concession.",
    rebateInfo: VIC_REBATE_INFO,
  },
  [State.NSW]: {
    heading: "Concession cards",
    body: "The NSW Government provides a range of concessions, rebates, and relief schemes to help reduce energy costs. You may be eligible if you hold a card issued by Centrelink or the Department of Veterans' Affairs.",
    eligibilityNote:
      "Concession card holders in New South Wales are eligible for Electricity and natural gas rebate.",
    eligibilityLink: eligibilityLink("nsw"),
    rebateInfo: NSW_REBATE_INFO,
  },
  [State.QLD]: {
    heading: "Concession cards",
    body: "The Qld Government provides a range of concessions, rebates, and relief schemes to help reduce energy costs. You may be eligible if you hold a Qld Seniors card or Immi card, or a card issued by Centrelink or the Department of Veterans' Affairs.",
    eligibilityNote:
      "Concession card holders in Queensland are eligible for Electricity and natural gas rebate.",
    eligibilityLink: eligibilityLink("qld"),
    rebateInfo: QLD_REBATE_INFO,
  },
  [State.ACT]: {
    heading: "Concession cards",
    body: "The ACT Government provides a range of concessions, rebates, and relief schemes to help reduce energy costs. You may be eligible if you hold a Services Access Card, or a card issued by Centrelink or the Department of Veterans' Affairs.",
    eligibilityNote:
      "Concession card holders in the ACT are eligible for Electricity, Gas and Water Rebate.",
    eligibilityLink: eligibilityLink("act"),
    rebateInfo: ACT_REBATE_INFO,
  },
  [State.SA]: {
    heading: "Concession cards",
    body: "The SA Government provides a range of concessions, rebates, and relief schemes to help reduce energy costs. You may be eligible if you hold a card or receive payments from Centrelink or the Department of Veterans' Affairs.",
    eligibilityNote:
      "Concession card holders in South Australia can get a guaranteed discount on electricity and natural gas rates*.",
    eligibilityLink: eligibilityLink("sa"),
    footerNote:
      "*For any questions about your concession eligibility, call Concessions SA on 1800 307 758. Information about eligibility for concessions for SA may change periodically. You can find the latest information on the SA Government website.",
    rebateInfo: SA_REBATE_INFO,
  },
};

export const getConcessionDisclaimer = (
  state: State | ""
): ConcessionDisclaimer | null => {
  if (!state) return null;
  return CONCESSION_DISCLAIMERS[state] ?? null;
};
